import React from 'react'
import PropTypes from 'prop-types'
import {
  AccountType,
  AppliedDiscountCodeType,
  BillingType,
  DONATION_CONTRIBUTION_AMOUNTS,
  MembershipType,
  ReferralType,
} from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { useHistory, Redirect } from 'react-router-dom'
import { SubmissionError } from 'redux-form'
import * as memberActions from 'member-actions'
import { selectors as memberSelectors } from 'member-reducer'
import { isEmpty } from 'lodash'
import { DiscountCode, PromoCodeWarningModal } from 'member-portal/components'
import { TermsAndConditionsForm } from 'member-portal/forms'
import { PATH, PAYMENT_STEP_TYPE, makeMemberApplicationApplyPath } from 'config'
import {
  computeBillingDetails,
  handleSubmitFailWithFlashMessage,
  parseNumberFromString,
  useToggle,
  useReferralEnabled,
} from 'utils'

const propTypes = {
  account: AccountType,
  basePath: PropTypes.string.isRequired,
  billing: BillingType,
  discountCodeDetails: AppliedDiscountCodeType,
  isTrialMembershipsActive: PropTypes.bool,
  membership: MembershipType.isRequired,
  membershipPrices: PropTypes.object,
  nextStep: PropTypes.string.isRequired,
  normalizedReferralNumber: PropTypes.string,
  promoCodeIsTrial: PropTypes.bool.isRequired,
  referralInfo: ReferralType,
  selectedMembershipType: PropTypes.string,
  selectedTrailerCareType: PropTypes.string,
  setBillingOptions: PropTypes.func.isRequired,
  setReferredByMemberNumber: PropTypes.func.isRequired,
  showReferral: PropTypes.bool,
  trailerCarePrices: PropTypes.object,
}

function TermsAndConditionStep({
  account = null,
  basePath,
  billing = null,
  discountCodeDetails = null,
  isTrialMembershipsActive = false,
  membership,
  membershipPrices = {},
  nextStep,
  normalizedReferralNumber = null,
  promoCodeIsTrial,
  referralInfo = null,
  selectedMembershipType = null,
  selectedTrailerCareType = null,
  setBillingOptions,
  setReferredByMemberNumber,
  showReferral = false,
  trailerCarePrices = {},
}) {
  const history = useHistory()
  const isReferralEnabled = useReferralEnabled()
  const [showPromoCodeWarningModal, togglePromoCodeWarningModal] = useToggle()

  // If user skips the boats page without entering in boat information, the
  // application should redirect to the Boat Page
  if (isEmpty(membership?.boats)) {
    return <Redirect to={makeMemberApplicationApplyPath(PATH.BOATS)} />
  }

  const handleTermsAcceptance = (formValues) => {
    if (
      discountCodeDetails?.autoRenewRequired &&
      !formValues.autoRenewalAgreement
    ) {
      togglePromoCodeWarningModal()
      throw new SubmissionError('Discount Code requires auto renewal')
    }

    // Set 'final donation' amount from either donationAmounts or
    // otherDollarDonationAmount and set 'subtotal' of membership price
    // with any discounts plus any trailer care package selected into
    // billing state
    const { otherDollarDonationAmount, donationAmounts } = formValues
    const finalDonation =
      otherDollarDonationAmount ?? parseNumberFromString(donationAmounts)

    const billingDetails = computeBillingDetails({
      account,
      billing: { ...billing, finalDonation },
      discountCodeDetails,
      membershipPrices,
      paymentStepType: PAYMENT_STEP_TYPE.NEW_MEMBERSHIP,
      selectedMembershipType,
      selectedTrailerCareType,
      trailerCarePrices,
    })

    const billingOptions = {
      ...formValues,
      ...billingDetails,
    }
    setBillingOptions(billingOptions)

    const { referredByMemberNumber } = formValues || {}
    setReferredByMemberNumber(referredByMemberNumber)
  }

  return (
    <>
      <>
        <DiscountCode
          formName="PaymentDiscount"
          {...{
            basePath,
            discountCodeDetails,
          }}
          {...(isTrialMembershipsActive && { promoCodeIsTrial })}
        />

        {promoCodeIsTrial && isTrialMembershipsActive && (
          <div
            className="trial-legal-language"
            dangerouslySetInnerHTML={{
              __html: discountCodeDetails?.promo_legal,
            }}
          />
        )}
        <TermsAndConditionsForm
          initialValues={setInitialTermsAndConditionsFormValues(
            normalizedReferralNumber,
            referralInfo,
            billing,
          )}
          showReferral={
            isReferralEnabled &&
            showReferral &&
            !promoCodeIsTrial
          }
          onSubmit={handleTermsAcceptance}
          onSubmitSuccess={() => {
            history.push(nextStep)
          }}
          onSubmitFail={handleSubmitFailWithFlashMessage}
        />
      </>
      {showPromoCodeWarningModal && (
        <PromoCodeWarningModal handleClose={togglePromoCodeWarningModal} />
      )}
    </>
  )
}

TermsAndConditionStep.propTypes = propTypes

function mapStateToProps(state) {
  return {
    account: memberSelectors.account(state),
    billing: memberSelectors.billing(state),
    discountCodeDetails: memberSelectors.discountCodeDetails(state),
    isTrialMembershipsActive: memberSelectors.isTrialMembershipsActive(state),
    membershipPrices: memberSelectors.membershipPrices(state),
    normalizedReferralNumber: memberSelectors.normalizedReferralNumber(state),
    promoCodeIsTrial: memberSelectors.promoCodeIsTrial(state),
    referralInfo: memberSelectors.referralInfo(state),
    selectedMembershipType: memberSelectors.selectedMembershipType(state),
    selectedTrailerCareType: memberSelectors.selectedTrailerCareType(state),
    trailerCarePrices: memberSelectors.trailerCarePrices(state),
  }
}

const mapDispatchToProps = {
  setBillingOptions: memberActions.setBillingOptions,
  setReferredByMemberNumber: memberActions.setReferredByMemberNumber,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  TermsAndConditionStep,
)

const setInitialTermsAndConditionsFormValues = (
  normalizedReferralNumber,
  referralInfo,
  billing = null,
) => {
  const initialValues = { ...billing }

  // Auto renew and donation
  initialValues.autoRenewalAgreement = true
  initialValues.donationAmounts = DONATION_CONTRIBUTION_AMOUNTS.NO_DONATION

  // Referral info
  initialValues.referredByMemberNumber = normalizedReferralNumber
  initialValues.fetchedReferrerMemberNumber =
    referralInfo?.fetchedReferrerMemberNumber
  initialValues.fetchedReferrerMemberName =
    referralInfo?.fetchedReferrerMemberName

  // Terms and Conditions & Auto Renewal Consent - always false when coming to 
  // this form
  initialValues.termsAgreement = false
  initialValues.autoRenewalConsent = false

  return initialValues
}
